import React, { useState } from 'react';
import { useSchoolPrideConfetti } from '@/lib/confetti';
import ProductLearnMore from './ProductLearnMore';

// Shared Eden-style checkout design primitives, used across every visit type's
// checkout so the look & feel stays consistent. See GlpCatalogCheckoutStep for
// the reference implementation.

const BRAND = '#2d8a5f';

export const Check = () => (
  <svg className="w-4 h-4 text-[#2d8a5f] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

// Product/medication thumbnail with a graceful placeholder. Renders the BE's
// imageUrl when present; falls back to the placeholder if it's missing OR fails
// to load (e.g. an expired S3 presigned URL) so the patient never sees a broken
// image.
export const MedThumb = ({ imageUrl, alt = 'Medication', size = 'w-16 h-16' }) => {
  const [failed, setFailed] = useState(false);
  if (imageUrl && !failed) {
    return <img src={imageUrl} alt={alt} onError={() => setFailed(true)} className={`${size} rounded-xl object-cover flex-shrink-0 bg-[#f0faf4]`} />;
  }
  return (
    <div className={`${size} rounded-xl bg-gradient-to-br from-[#e8f5ee] to-[#cfeadd] flex items-center justify-center flex-shrink-0`} aria-hidden="true">
      <svg className="w-8 h-8 text-[#2d8a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 3h6M10 3v4.5L5.6 16a3 3 0 002.6 4.5h7.6a3 3 0 002.6-4.5L14 7.5V3M7.5 13h9" />
      </svg>
    </div>
  );
};

export const BenefitList = ({ items, className = '' }) => (
  <ul className={`space-y-2 text-sm text-gray-700 ${className}`}>
    {items.map((t, i) => (
      <li key={i} className="flex gap-2"><Check /> {t}</li>
    ))}
  </ul>
);

// Amber reassurance banner (e.g. free shipping / money-back).
export const TrustBanner = ({ children }) => (
  <div className="flex items-center gap-3 rounded-2xl bg-[#fff7ed] border border-[#fed7aa] p-4 mb-4">
    <div className="w-9 h-9 rounded-full bg-[#fed7aa] flex items-center justify-center flex-shrink-0">
      <svg className="w-5 h-5 text-[#c2410c]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
    </div>
    <p className="text-sm text-gray-700">{children}</p>
  </div>
);

// §preference-not-prescription (docs/BACKEND_INTAKE_SELECTION_IS_A_PREFERENCE_PRD.md):
// The product a patient picks at checkout is a REQUEST, not the prescription —
// a licensed provider reviews the intake and may prescribe something different
// (on visit 81969607 the patient selected an injection and was prescribed a
// topical cream). Render this under the medication card in every checkout that
// shows a product choice, so the eventual Rx doesn't read as a fulfilment error.
export const ProviderReviewNotice = ({ className = '' }) => (
  <p className={`text-xs text-gray-500 leading-relaxed ${className}`} data-testid="provider-review-notice">
    Preferred option — subject to provider review. A licensed provider reviews
    your intake and prescribes the treatment that&rsquo;s right for you, which may
    differ from your selection.
  </p>
);

// §membership disclosure (docs/BACKEND_MEMBERSHIP_SUBSCRIPTION_PRD.md §11):
// mandatory $59/mo Fitpal Membership line shown in every medication checkout's
// order summary. GATED DARK behind REACT_APP_MEMBERSHIP_ENABLED='1' — do NOT
// enable until the BE membership charge (CreateCard + CreateSubscription) is
// live, or the disclosure would describe a charge that doesn't happen.
export const MEMBERSHIP_ENABLED = process.env.REACT_APP_MEMBERSHIP_ENABLED === '1';
export const MEMBERSHIP_FEE_LABEL = '$59/mo';

// §subscriptions (docs/BACKEND_SQUARE_SUBSCRIPTIONS_BILLING_PRD.md): refill-
// triggered auto-billing needs a card on file, which requires the SCA
// verificationToken from verifyBuyer at checkout. That step is otherwise only
// run for membership, so gate it on EITHER flag. Default OFF — flip this on
// (Vercel env) in lockstep with the BE's SQUARE_SUBSCRIPTIONS_ENABLED so the
// live tokenization path is unchanged until subscriptions actually go live.
export const SUBSCRIPTIONS_ENABLED = process.env.REACT_APP_SUBSCRIPTIONS_ENABLED === '1';
// True when checkout must capture a card on file (membership OR subscriptions).
export const CARD_ON_FILE_ENABLED = MEMBERSHIP_ENABLED || SUBSCRIPTIONS_ENABLED;

// §extra-payment-methods (docs/BACKEND_SQUARE_PAYMENTS_METHODS_AND_COUPONS_PRD):
// offer Apple Pay / Google Pay / Cash App Pay / Afterpay alongside the card.
// Dark by default — needs the Square account to enable each method (+ Apple Pay
// domain registration). Flip in Vercel once BE/ops confirm the methods are live.
export const EXTRA_PAY_METHODS_ENABLED = process.env.REACT_APP_EXTRA_PAY_METHODS === '1';

// §coupons (Square-native discounts): show a coupon-code field at checkout. Dark
// until the BE /payments/validate-coupon + discounted-capture path ships.
export const COUPONS_ENABLED = process.env.REACT_APP_COUPONS_ENABLED === '1';

export const MembershipNotice = ({ coversConsult = false }) => {
  if (!MEMBERSHIP_ENABLED) return null;
  return (
    <div className="mt-2 pt-3 border-t border-gray-100" data-testid="membership-notice">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">Fitpal Membership</span>
        <span className="font-medium">{MEMBERSHIP_FEE_LABEL}</span>
      </div>
      <p className="text-xs text-gray-400 mt-1">
        {coversConsult
          ? "You're enrolling in Fitpal Membership — $59/month, which includes your provider consults. Your medication is filled at your pharmacy with your insurance; there's no charge from Fitpal today. Auto-renews monthly; cancel anytime."
          : 'Includes your provider consults. Auto-renews monthly starting today, billed separately from your medication. Cancel anytime.'}
      </p>
    </div>
  );
};

// Default reassurance bullets used on the recommendation slide.
export const DEFAULT_ABOUT = [
  'Reviewed by a licensed U.S. provider before anything ships',
  'Dispensed by licensed U.S. pharmacies',
  'Physician oversight with a plan personalized to you',
];

// Two-phase checkout wrapper (Eden-style):
//   1. RECOMMEND — "Your provider will likely recommend treatment." + a product
//      summary the flow passes in (name/sub/price/imageUrl) + "Select this
//      treatment". Optional "See other options" link (onSeeOptions).
//   2. PAY — renders `children` (the flow's actual checkout/payment UI).
// Pass `headline`/`subhead` to override copy. Children mount only in the pay
// phase, so the payment form (Square) doesn't initialize early.
export const RecommendationGate = ({
  product,                 // { name, sub, price (string, e.g. "$80.00"), imageUrl }
  learnMoreProduct,        // optional catalog item (with medId) → "Learn more" link
  headline = 'Your provider will likely recommend treatment.',
  subhead = "Select the treatment you'd like your medical provider to consider prescribing.",
  aboutItems = DEFAULT_ABOUT,
  selectLabel = 'Select this treatment',
  onSeeOptions,            // optional () => void
  seeOptionsLabel = 'See other options',
  children,
}) => {
  const [phase, setPhase] = useState('recommend');
  useSchoolPrideConfetti(phase === 'recommend'); // celebrate the recommendation slide
  if (phase === 'pay') return <>{children}</>;

  return (
    <div data-testid="recommendation-slide" className="max-w-md mx-auto">
      <div className="mb-6">
        <p className="text-[#2d8a5f] font-semibold text-sm mb-1">Congratulations!</p>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">{headline}</h2>
        <p className="text-gray-500 text-sm mt-2">{subhead}</p>
      </div>

      {product && (
        <div className="rounded-3xl border-2 border-[#2d8a5f] bg-[#f0faf4] p-5 mb-4">
          <div className="flex items-center gap-4">
            <MedThumb imageUrl={product.imageUrl} alt={product.name} size="w-20 h-20" />
            <div className="min-w-0 flex-1">
              <p className="font-bold text-gray-900 leading-snug">{product.name}</p>
              {product.sub && <p className="text-sm text-gray-500">{product.sub}</p>}
              {product.price && <p className="text-lg font-bold text-[#2d8a5f] mt-1">{product.price}</p>}
              {learnMoreProduct && <ProductLearnMore product={learnMoreProduct} className="mt-1 inline-block" />}
            </div>
          </div>
        </div>
      )}

      <button type="button" data-testid="select-treatment" onClick={() => setPhase('pay')}
        className="w-full rounded-2xl bg-[#2d8a5f] px-4 py-4 font-semibold text-white hover:bg-[#246f4d] transition-colors mb-2">
        {selectLabel}
      </button>
      {onSeeOptions && (
        <button type="button" onClick={onSeeOptions} data-testid="see-options" className="w-full text-center text-sm text-[#2d8a5f] underline underline-offset-2 hover:text-[#236b4a] mb-6">
          {seeOptionsLabel}
        </button>
      )}

      {aboutItems && aboutItems.length > 0 && (
        <div className="rounded-2xl bg-gray-50 p-5 mt-4">
          <h3 className="font-semibold text-gray-900 mb-3 text-sm">About your treatment</h3>
          <BenefitList items={aboutItems} />
        </div>
      )}
    </div>
  );
};

export { BRAND };
